/* eslint-disable no-extra-boolean-cast */
/**
 * PrintService
 * 
 * Handles receipt printing for web and Electron platforms
 * - For web: Generates HTML receipt and opens print dialog
 * - For Electron: Sends receipt data to native thermal printer via IPC
 * 
 * @module PrintService
 */
import { isElectron } from './Utils';

/*
<div class="center qrcode">
  <img src="${qrcode}" width="98" height="98" />
  <div class="center footer">Kindly give your valuable feedback</div>
</div>
*/

/**
 * Generates HTML receipt for printing
 * @param {Object} order - Order object with details (BillNo, items, totals, etc.)
 * @returns {string} HTML string formatted for thermal printer
 */
function generateReceiptHTML(order) {
  const itemsHTML = order?.SalesDetList.map(item => {
    return `<tr><td class="ItemName semiBold">${item.ItemName}</td><td class="center ItemQty semiBold">${item.Qty}</td><td class="center ItemPrice semiBold">${item.Rate}</td><td class="right ItemAmount semiBold">${item.Amount}</td></tr>`;
  }).join('');

  return `
  <html>
    <head>
      <title>Receipt</title>
      <style>
        * { font-family: 'Courier New', monospace; font-size: 12px; }
        body { width: '100%'; margin: 0px 4px auto; }
        .center { text-align: center; }
        .capitalize { text-transform: capitalize; }
        .right { text-align: right; }
        .left { text-align: left; }
        .bold { font-weight: bold; }
        .semiBold {font-weight: 600;}
        .line { border-top: 1px dashed #000; margin: 5px 0; }
        table { width: 100%; border-collapse: collapse; }
        .fs-10 {font-size: 10px}
        th {border-bottom: 1px dashed #000; margin: 5px 0;}
        td.ItemName {word-break: break-word; width: 40%;text-transform: capitalize;}
        td.ItemQty {word-break: break-word; width: 10%;}
        td.ItemPrice {word-break: break-word; width: 25%;}
        td.ItemAmount {word-break: break-word; width: 25%;}
        .cancelled { text-decoration: line-through; color: #888; }
        .cancel-note { font-size: 10px; color: #888; }
        .order-id { font-size: 12px; font-weight: bold; }
        .qrcode { text-align: center; margin-top: 10px; }
        .flex { display: flex; flex-direction: row; justify-content: space-between }
      </style>
    </head>
    <body>
      ${!!order.isDuplicatePrint ? '<div class="center bold">Duplicate</div>' : ''}
      <div class="center order-id">Order ID: ${order.BillNo}</div>
      ${!!order.VendorName ? `<div class="center bold capitalize">${order.VendorName}</div>` : ''}
      <div class="center bold capitalize">${order.CafeName}</div>
       ${!!order.Address1 ? `<div class="center bold">${order.Address1}</div>` : ''}
      <div class="center bold capitalize">${order.CanteenName}</div>
      ${!!order.GSTN ? `<div class="center bold">GSTIN:${order.GSTN}</div>` : ''}
      ${!!order.FssaiNo ? `<div class="center bold">FSSAI:${order.FssaiNo}</div>` : ''}
      <div class="center semiBold">Date: ${order?.DateTime}</div>
      <div class="line"></div>
      <table>
      <tr><th class="ItemName left fs-10 semiBold">Item Name</th><th class="center ItemQty fs-10 semiBold">Qty</th><th class="center ItemPrice fs-10 semiBold">Rate</th><th class="right ItemAmount fs-10 semiBold">Amount</th></tr>
      <tbody>${itemsHTML}</tbody>
      </table>
      <div class="line"></div>
      <table>
        ${order?.SubTotal > 0 ? `<tr><td class="semiBold">SubTotal</td><td class="right semiBold">${order?.SubTotal}</td></tr>` : ''}
        ${order?.SGSTAmt > 0 ? `<tr><td class="semiBold">SGST(${order?.SGSTPercentage}%)</td><td class="right semiBold">${order?.SGSTAmt}</td></tr>` : ''}
        ${order?.CGSTAmt > 0 ? `<tr><td class="semiBold">CGST(${order?.CGSTPercentage}%)</td><td class="right semiBold">${order?.CGSTAmt}</td></tr>` : ''}
        ${order?.Discount > 0 ? `<tr><td class="bold">Discount</td><td class="right bold">${order?.Discount}</td></tr>` : ''}
        ${order?.ParcelCharges > 0 ? `<tr><td class="bold">Parcel Charges</td><td class="right bold">${order?.ParcelCharges}</td></tr>` : ''}
        ${order?.GrandTotal > 0 ? `<tr><td class="bold">Grand Total</td><td class="right bold">${order?.GrandTotal}</td></tr>` : ''}
      </table>
      <div class="line"></div>
      ${!!order?.GSTInfo ? `<div class="left semiBold">${order?.GSTInfo}</div>` : ''}
      ${!!order?.Note ? `<div class="left semiBold">Note:: ${order?.Note}</div>` : ''}
      <div class="center footer semiBold">Kindly give your valuable feedback</div>
      <div class="center footer semiBold">Thank you! Visit Again</div>
    </body>
  </html>
  `;
}

/**
 * Generates HTML KOT (Kitchen Order Ticket) for printing
 * @param {Object} order - Order object with items list
 * @returns {string} HTML string formatted for kitchen printer
 */
function generateOrderKOTHTML(order) {
  const itemsHTML = order?.SalesDetList.map(item => {
    return `<tr><td class="ItemName left semiBold">${item.ItemName}</td><td class="right ItemQty semiBold">${item.Qty}</td></tr>`;
  }).join('');

  return `
  <html>
    <head>
      <title>Receipt</title>
      <style>
        * { font-family: 'Courier New', monospace; font-size: 12px; }
        body { width: '100%'; margin: 0px 4px auto; }
        .center { text-align: center; }
        .capitalize { text-transform: capitalize; }
        .right { text-align: right; }
        .left { text-align: left; }
        .bold { font-weight: bold; }
        .semiBold {font-weight: 600;}
        .line { border-top: 1px dashed #000; margin: 5px 0; }
        table { width: 100%; border-collapse: collapse; }
        .fs-10 {font-size: 10px}
        th {border-bottom: 1px dashed #000; margin: 5px 0;}
        td.ItemName {word-break: break-word; width: 70%;text-transform: capitalize;}
        td.ItemQty {word-break: break-word; width: 30%}
        th.ItemName {word-break: break-word; width: 70%;}
        th.ItemQty {word-break: break-word; width: 30%}
        .cancelled { text-decoration: line-through; color: #888; }
        .cancel-note { font-size: 10px; color: #888; }
        .order-id { font-size: 12px; font-weight: bold; }
        .qrcode { text-align: center; margin-top: 10px; }
      </style>
    </head>
    <body>
      ${!!order.isDuplicatePrint ? '<div class="center bold">Duplicate</div>' : ''}
      <div class="center order-id">Order ID: ${order.BillNo}</div>
      <div class="center bold capitalize">${order.CanteenName}</div>
      <div class="center semiBold">${order?.DateTime}</div>
      <div class="line"></div>
      <table>
      <tr><th class="ItemName left fs-10 semiBold">Item Name</th><th class="ItemQty right fs-10 semiBold">Qty</th></tr>
      <tbody>${itemsHTML}</tbody>
      </table>
      <div class="line"></div>
      <div class="center footer semiBold">Thank you! Visit Again</div>
    </body>
  </html>
  `;
}

/**
 * Prints order receipt and optionally KOT
 * Works with both Electron desktop and web print service
 * @param {Object} data - Order data object
 * @param {boolean} [data.ENABLE_KOT_PRINT] - Whether to print KOT ticket
 * @returns {Promise<boolean>} True if print successful, false otherwise
 */
export const printOrderData = (data) => new Promise(async (resolve) => {
  try {
    const _isElectron = isElectron();

    if (!!data && typeof data === 'object') {
      let printTasks = [];
      if (_isElectron && window?.electronAPI?.printOrder) {
        printTasks = [
          window?.electronAPI?.printOrder({ order: data }),
        ];
        if (!!data?.ENABLE_KOT_PRINT) {
          printTasks.push(window?.electronAPI?.printKOTOrder({ order: data }));
        }
      } else if (typeof window !== 'undefined' && window?.PrintService?.printHTML) {
        printTasks = [
          window?.PrintService?.printHTML(generateReceiptHTML(data)),
        ];
        if (!!data?.ENABLE_KOT_PRINT) {
          printTasks.push(window?.PrintService?.printHTML(generateOrderKOTHTML(data)));
        }
      } else {
        // Print service not available
        return resolve(false);
      }

      const results = await Promise.all(printTasks);
      const allSuccess = results.every(result => result === true);
      return resolve(allSuccess);
    }

    return resolve(false);
  } catch (error) {
    console.error(error);
    return resolve(false);
  }
});

/**
 * Prints item-wise sales report (Electron desktop only)
 * @param {Object} data - Report data with item statistics
 * @returns {Promise<boolean>} True if print successful, false otherwise
 */
export const printItemWiseReport = (data) => new Promise(async (resolve) => {
  try {
    const _isElectron = isElectron();
    if (!!data && typeof data === 'object') {
      let printTasks = [];
      if (_isElectron && window?.electronAPI?.printItemWiseReport) {
        printTasks = [
          window?.electronAPI?.printItemWiseReport({ data }),
        ];
      }
      const results = await Promise.all(printTasks);
      const allSuccess = results.every(result => result === true);
      return resolve(allSuccess);
    }
    return resolve(false);
  } catch (error) {
    console.error(error);
    return resolve(false);
  }
});

/**
 * Prints sales summary report (Electron desktop only)
 * @param {Object} data - Sales report data with totals and transactions
 * @returns {Promise<boolean>} True if print successful, false otherwise
 */
export const printSalesReport = (data) => new Promise(async (resolve) => {
  try {
    const _isElectron = isElectron();
    if (!!data && typeof data === 'object') {
      let printTasks = [];
      if (_isElectron && window?.electronAPI?.printSalesReport) {
        printTasks = [
          window?.electronAPI?.printSalesReport({ data }),
        ];
      }
      const results = await Promise.all(printTasks);
      const allSuccess = results.every(result => result === true);
      return resolve(allSuccess);
    }
    return resolve(false);
  } catch (error) {
    console.error(error);
    return resolve(false);
  }
});

/**
 * Prints order receipt and optionally KOT
 * Works with both Electron desktop and web print service 
 * @param {*} data 
 * @returns 
 */
export const printInventoryItemsReport = (data) => new Promise(async (resolve) => {
  try {
    const _isElectron = isElectron();
    if (!!data && typeof data === 'object') {
      let printTasks = [];
      if (_isElectron && window?.electronAPI?.printInventoryItemsReport) {
        printTasks = [
          window?.electronAPI?.printInventoryItemsReport({ data }),
        ];
      }
      const results = await Promise.all(printTasks);
      const allSuccess = results.every(result => result === true);
      return resolve(allSuccess);
    }
    return resolve(false);
  } catch (error) {
    console.error(error);
    return resolve(false);
  }
});

/**
 * Prints order receipt
 * Works with both Electron desktop and web print service
 * @param {*} data 
 * @returns 
 */
export const printCountersReport = (data) => new Promise(async (resolve) => {
  try {
    const _isElectron = isElectron();
    if (!!data && typeof data === 'object') {
      let printTasks = [];
      if (_isElectron && window?.electronAPI?.printCountersReport) {
        printTasks = [
          window?.electronAPI?.printCountersReport({ data }),
        ];
      }
      const results = await Promise.all(printTasks);
      const allSuccess = results.every(result => result === true);
      return resolve(allSuccess);
    }
    return resolve(false);
  } catch (error) {
    console.error(error);
    return resolve(false);
  }
});
